import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useAuth from '../../../hooks/useAuth';
import logo from '../../../images/logo.png';
import authBg from '../../../images/auth-bg.png';

const Register = () => {
    const [loginData, setLoginData] = useState({});
    const { user, registerUser, isLoading, authError, signInUsingGoogle } = useAuth();

    const location = useLocation();
    const navigate = useNavigate();

    const handleOnBlur = e => {
        const field = e.target.name;
        const value = e.target.value;
        const newLoginData = { ...loginData };
        newLoginData[field] = value;
        setLoginData(newLoginData);
    }

    const handleLoginSubmit = e => {
        e.preventDefault();
        if (loginData.password !== loginData.confirmPassword) {
            alert('Your password did not match');
            return;
        }
        registerUser(loginData.email, loginData.password, loginData.name, navigate);
    }

    const handleGoogleLogin = () => {
        signInUsingGoogle()
            .then(() => {
                const destination = location.state?.from || '/';
                navigate(destination);
            })
    }

    return (
        <div
            className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.6), rgba(15, 23, 42, 0.6)), url(${authBg})` }}
        >
            <div className="max-w-md w-full space-y-8 backdrop-blur-md bg-white/10 p-10 rounded-2xl border border-white/20 shadow-2xl">
                <div className="text-center">
                    <img className="mx-auto h-20 w-auto object-contain drop-shadow-md" src={logo} alt="GypsyTravels Logo" />
                    <h2 className="mt-6 text-3xl font-extrabold text-white">
                        Create <span className="text-amber-400">Account</span>
                    </h2>
                    <p className="mt-2 text-sm text-slate-200">
                        Join <span className="font-bold">GypsyTravels</span> and start your adventure
                    </p>
                </div>

                {!isLoading && <form onSubmit={handleLoginSubmit} className="mt-8 space-y-6">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-slate-300 text-sm font-medium mb-1">Full Name</label>
                            <input
                                type="text"
                                name="name"
                                onBlur={handleOnBlur}
                                required
                                placeholder="John Doe"
                                className="appearance-none relative block w-full px-4 py-3 border border-white/10 placeholder-slate-400 text-white bg-white/5 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent sm:text-sm transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-slate-300 text-sm font-medium mb-1">Email Address</label>
                            <input
                                type="email"
                                name="email"
                                onBlur={handleOnBlur}
                                required
                                placeholder="name@example.com"
                                className="appearance-none relative block w-full px-4 py-3 border border-white/10 placeholder-slate-400 text-white bg-white/5 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent sm:text-sm transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-slate-300 text-sm font-medium mb-1">Password</label>
                            <input
                                type="password"
                                name="password"
                                onBlur={handleOnBlur}
                                required
                                placeholder="••••••••"
                                className="appearance-none relative block w-full px-4 py-3 border border-white/10 placeholder-slate-400 text-white bg-white/5 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent sm:text-sm transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-slate-300 text-sm font-medium mb-1">Confirm Password</label>
                            <input
                                type="password"
                                name="confirmPassword"
                                onBlur={handleOnBlur}
                                required
                                placeholder="••••••••"
                                className="appearance-none relative block w-full px-4 py-3 border border-white/10 placeholder-slate-400 text-white bg-white/5 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent sm:text-sm transition-all"
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-lg text-slate-900 bg-amber-400 hover:bg-amber-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all duration-300 transform hover:scale-[1.02]"
                    >
                        Sign Up
                    </button>

                    <div className="relative">
                        <div className="absolute inset-0 flex items-center" aria-hidden="true">
                            <div className="w-full border-t border-white/10"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-transparent text-slate-400 italic">OR</span>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={handleGoogleLogin}
                        className="group relative w-full flex justify-center py-3 px-4 border border-white/20 text-sm font-medium rounded-lg text-white bg-white/5 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all duration-300"
                    >
                        <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                            <i className="fab fa-google text-amber-400 group-hover:text-amber-300 transition-colors"></i>
                        </span>
                        Sign up with Google
                    </button>

                    {authError && <p className="text-red-500 text-center mt-2 bg-red-500/10 py-2 rounded-lg border border-red-500/20">{authError}</p>}

                    <div className="text-center">
                        <p className="text-sm text-slate-300">
                            Already have an account?{' '}
                            <Link to="/signin" className="font-bold text-amber-400 hover:text-amber-300 transition-colors">
                                Sign In
                            </Link>
                        </p>
                    </div>
                </form>}
                {isLoading && (
                    <div className="flex justify-center py-10">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Register;